import React from "react"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { tableTypes } from "../../../pages/PnlReporting/constants"
import {
  generatePNLReportingDetailCols,
  generateReportingCols,
  transactionTableCols,
} from "./tableConfigs"
import { Button } from "@mui/material"
import {
  setReportingScrollTop,
  setSelectedLineItem,
  setTableType,
} from "../../../redux/slices/pnlReportingSlice"

const saveReportingScrollPosition = (
  dispatch: ReturnType<typeof useAppDispatch>,
) => {
  const container = document.querySelector(
    "[data-pnl-reporting-scroll]",
  ) as HTMLDivElement | null
  if (container) {
    dispatch(setReportingScrollTop(container.scrollTop))
  }
}
import { _PNLTable } from "./Atom"
import { PnlReportingState } from "../../../pages/PnlReporting/types"
import PermsWrapper from "../../Auth/PermsWrapper"
import { permissions } from "../../../../config/constants"

type pnlTableProps = {
  table: string
}

const PNLTable = (props: pnlTableProps) => {
  const dispatch = useAppDispatch()
  const state: PnlReportingState = useAppSelector((state) => state.pnlReporting)

  const {
    reportDetail,
    pnlReportinigTableData,
    reportingAggregations,
    years,
    selectedLineItem,
  } = state
  const { transactionData, reportDetailAggregate, pnlReportingSelectedClass } =
    state

  const reportingCols = React.useMemo(() => {
    return generateReportingCols(reportingAggregations, years?.selected?.label)
  }, [reportingAggregations])

  const reportingDetailCols = React.useMemo(() => {
    return generatePNLReportingDetailCols(
      reportDetail,
      reportDetailAggregate,
      dispatch,
    )
  }, [reportDetail, reportDetailAggregate])

  const totalTransactionAmount = React.useMemo<string>(() => {
    return transactionData
      .reduce<number>(
        (total: number, obj: any) =>
          total + parseFloat(obj.transaction_amount || "0"),
        0,
      )
      .toFixed(2)
  }, [transactionData])

  const transactionCols = React.useMemo(
    () => transactionTableCols(totalTransactionAmount),
    [totalTransactionAmount],
  )

  const disableDetail = React.useMemo(() => {
    return (
      pnlReportingSelectedClass?.class_id === "office_total" ||
      pnlReportingSelectedClass?.class_id === "all_total"
    )
  }, [pnlReportingSelectedClass])

  const { table } = props
  switch (table) {
    case tableTypes.REPORTING:
      reportingCols[0].Cell = ({ renderedCellValue, row }) => {
        return (
          <Button
            disableTouchRipple
            variant="text"
            sx={{
              fontWeight: "600",
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              color: row.index ? "" : "black",
              justifyContent: "flex-start",
              pl: "20px",
              "&:Hover": {
                cursor: disableDetail ? "not-allowed" : "pointer",
                backgroundColor: disableDetail ? "transparent" : undefined,
              },
            }}
            onClick={
              disableDetail
                ? undefined
                : () => {
                    saveReportingScrollPosition(dispatch)
                    dispatch(setSelectedLineItem(renderedCellValue?.toString()))
                    dispatch(setTableType(tableTypes.DETAIL))
                  }
            }
          >
            {renderedCellValue}
          </Button>
        )
      }
      return (
        <_PNLTable
          table={table}
          columns={reportingCols}
          data={pnlReportinigTableData}
          pinningCols={["line_item"]}
          highlightedLineItem={selectedLineItem}
        />
      )
    case tableTypes.DETAIL:
      return (
        <_PNLTable
          columns={reportingDetailCols}
          data={reportDetail}
          pinningCols={["account-class", "account-sub-class", "TTM"]}
          table={table}
        />
      )
    case tableTypes.SUBDETAIL:
      return (
        <_PNLTable
          columns={transactionCols}
          data={transactionData}
          pinningCols={[]}
          table={table}
        />
      )
    default:
      break
  }
}

export default PNLTable
